import type { RouteRecordRaw } from 'vue-router'
import { getRouteMetaFromConfig } from '../config/seo.i18n'
import { localServiceRoutes } from './localServiceRoutes'
import { localCityHubRoute } from './localCityHubRoute'
import { localCommuneLegacyRedirect } from './localCommuneLegacyRedirect'
import { TOOL_DEFINITIONS, TOOL_NOINDEX_IDS } from '../config/tools'
import { withBrandTitle } from '../config/seoTitle'

function meta(routeName: string, extra: Record<string, unknown> = {}) {
  const config = getRouteMetaFromConfig(routeName)
  if (!config) {
    throw new Error(`Meta SEO manquante pour la route "${routeName}" dans seo.routes.json`)
  }
  return {
    title: config.title,
    description: config.description,
    robots: config.robots,
    ...extra
  }
}

/** Descriptions SEO (le title est toujours `{nom de l'outil} | PostDev`). */
const TOOL_SEO_DESCRIPTIONS: Record<string, string> = {
  'meta-tags': 'Analysez vos balises title, description, Open Graph et Twitter Card en un clic. Outil meta SEO gratuit en ligne.',
  'robots-txt': 'Générez un robots.txt sur mesure pour contrôler l\'indexation de votre site. Outil gratuit, résultat immédiat.',
  'tech-detector': 'Identifiez le CMS, les frameworks, CDN et bibliothèques utilisés par n\'importe quel site. Détection gratuite.',
  'web-server': 'Découvrez le logiciel serveur web derrière un domaine. Outil gratuit : Nginx, Apache, IIS, Cloudflare détectés.',
  'analytics-checker': 'Vérifiez la présence de Google Analytics et autres outils d\'analyse. Gratuit, sans inscription.',
  'ip-lookup': 'Obtenez l\'adresse IP, le pays et les informations d\'hébergement de n\'importe quel nom de domaine. Lookup IP gratuit.',
  'dns-lookup': 'Interrogez tous les enregistrements DNS d\'un domaine en temps réel. DNS Lookup gratuit, sans limite.',
  'domain-age': 'Découvrez la date d\'enregistrement et l\'ancienneté d\'un domaine via RDAP. Outil gratuit, résultat instantané.',
  'ping-tester': 'Mesurez le temps de réponse et la disponibilité HTTP de votre site. Test de ping en ligne, gratuit.',
  'dmarc-checker': 'Contrôlez si votre domaine dispose d\'une politique DMARC correctement configurée. Vérification DNS gratuite.',
  'spf-checker': 'Vérifiez la configuration SPF de votre domaine pour protéger votre messagerie. Outil gratuit.',
  'ssl-checker': 'Analysez la validité, l\'émetteur et la date d\'expiration du certificat SSL de votre site. Vérificateur gratuit.',
  'https-redirect': 'Vérifiez que votre site redirige correctement de HTTP vers HTTPS. Test de redirection gratuit en ligne.',
  'htaccess-checker': 'Vérifiez les règles de redirection de votre fichier .htaccess. Outil d\'analyse gratuit.',
  'email-scanner': 'Repérez les e-mails exposés dans le code source de votre site pour limiter le spam. Scanner gratuit.',
  'server-version': 'Vérifiez si votre serveur expose sa version dans les en-têtes HTTP, une faille de sécurité courante.',
  'open-graph': 'Contrôlez le titre, l\'image et la description Open Graph qui s\'affichent lors d\'un partage Facebook. Outil gratuit.',
  'facebook-pixel': 'Confirmez que le Pixel Meta (Facebook) est bien installé et actif sur votre site. Vérificateur gratuit.',
  'twitter-cards': 'Contrôlez l\'aperçu de votre page lors d\'un partage sur X (Twitter) : titre, description, image. Outil gratuit.',
  'perf-speed': 'Analysez les performances de chargement de votre page et identifiez les ralentissements. Outil gratuit.',
  'perf-gzip': 'Contrôlez si votre serveur compresse bien les fichiers HTML, CSS et JS. Test GZIP gratuit.',
  'perf-http2': 'Détectez si votre site bénéficie du protocole HTTP/2 ou HTTP/3 pour des chargements plus rapides.',
  'perf-images': 'Vérifiez que les images de votre page sont bien optimisées (format, taille, lazy-load). Outil gratuit.',
  'perf-minify': 'Contrôlez si vos fichiers CSS et JS sont minifiés pour réduire les temps de chargement. Analyse gratuite.',
  'ux-responsive': 'Vérifiez l\'affichage responsive de votre site sur mobile, tablette et desktop. Test gratuit.',
  'ux-cwv': 'Évaluez vos Core Web Vitals par rapport aux seuils Google. Outil d\'estimation gratuit.',
  'ux-favicon': 'Contrôlez la présence et la configuration correcte du favicon de votre site. Vérification gratuite.',
  'onpage-title': 'Analysez et optimisez votre balise title HTML pour améliorer votre classement Google. Outil SEO gratuit.',
  'onpage-meta-description': 'Contrôlez votre meta description et optimisez-la pour améliorer le taux de clic dans Google. Outil SEO gratuit.',
  'onpage-canonical': 'Détectez et validez la balise canonical de votre page pour éviter le contenu dupliqué. Outil SEO gratuit.',
  'onpage-serp': 'Simulez l\'apparence de votre résultat dans les pages Google avant publication. Aperçu SERP gratuit.',
  'onpage-schema': 'Identifiez les données structurées (JSON-LD, Microdata) et vérifiez leur conformité Schema.org. Gratuit.',
  'onpage-sitemap-check': 'Contrôlez si votre site dispose bien d\'un sitemap XML accessible. Vérification automatique gratuite.',
  'onpage-sitemap-validate': 'Analysez votre sitemap XML à la recherche d\'erreurs et d\'URL inaccessibles. Validateur gratuit.',
  'onpage-keyword-density': 'Mesurez la fréquence et la densité de vos mots-clés pour optimiser votre contenu SEO. Outil gratuit.',
  'onpage-internal-links': 'Comptez et qualifiez les liens internes et externes d\'une page web (follow/nofollow). Outil SEO gratuit.',
  'gen-meta-tags': 'Créez automatiquement vos balises title, description et Open Graph optimisées pour le SEO. Générateur gratuit.',
  'gen-htaccess': 'Créez un fichier .htaccess personnalisé (redirections, HTTPS, cache, sécurité). Téléchargement gratuit.',
  'gen-robots-txt': 'Générez un fichier robots.txt optimisé pour contrôler l\'indexation de votre site. Gratuit.',
  'gen-sitemap': 'Créez un sitemap XML complet à partir de la liste de vos pages. Téléchargement immédiat, gratuit.',
  'gen-keywords': 'Extrayez automatiquement les mots-clés les plus fréquents de votre contenu. Outil SEO gratuit.',
  'gen-legal': 'Créez une base de mentions légales et politique de confidentialité RGPD en quelques clics. Gratuit.',
  'gen-cgu-cgv': 'Générez des conditions générales d\'utilisation et de vente personnalisées pour votre activité. Gratuit.',
  'gen-specs': 'Structurez votre projet digital en cahier des charges prêt à soumettre à un prestataire. Gratuit.',
  'sim-carbon': 'Estimez l\'impact environnemental de votre site et découvrez les leviers d\'amélioration. Calculateur gratuit.',
  'sim-conversion': 'Projetez votre CA en fonction du trafic et du taux de conversion. Simulateur web gratuit.',
  'sim-budget': 'Obtenez une estimation budgétaire pour votre projet digital. Simulateur de budget gratuit.',
  'real-speed': 'Mesurez les performances de votre site en conditions réelles, côté client. Test de vitesse gratuit.',
  'site-obsolete': 'Évaluez l\'obsolescence de votre site (technologies, design, sécurité, UX). Diagnostic gratuit.',
  'wp-security': 'Détectez les failles courantes, plugins vulnérables et mauvaises configurations WordPress. Scan gratuit.',
}

function toolMeta(toolId: string, extra: Record<string, unknown> = {}) {
  const tool = TOOL_DEFINITIONS.find((t) => t.id === toolId)
  const noindex = (TOOL_NOINDEX_IDS as readonly string[]).includes(toolId)
  return {
    title: withBrandTitle(tool?.name || 'Outil SEO et web'),
    description:
      TOOL_SEO_DESCRIPTIONS[toolId] ??
      tool?.description ??
      'Outil SEO et web gratuit en ligne par PostDev.',
    robots: noindex ? 'noindex,follow' : 'index,follow',
    toolId,
    ...extra
  }
}

// Routes publiques (chemins relatifs ; le routeur les expose en absolu /, /services, …)
export const publicRoutes: RouteRecordRaw[] = [
  {
    path: '',
    name: 'home',
    component: () => import('../views/HomeView.vue'),
    meta: meta('home')
  },
  {
    path: 'services',
    name: 'services',
    component: () => import('../views/ServicesView.vue'),
    meta: meta('services')
  },
  {
    path: 'about',
    name: 'about',
    component: () => import('../views/AboutView.vue'),
    meta: meta('about')
  },
  {
    path: 'contact',
    name: 'contact',
    component: () => import('../views/ContactView.vue'),
    meta: meta('contact')
  },
  {
    path: 'blog',
    name: 'blog',
    component: () => import('../views/BlogView.vue'),
    meta: meta('blog')
  },
  {
    path: 'blog/:slug',
    name: 'blog-post',
    component: () => import('../views/BlogPostView.vue'),
    meta: meta('blog-post')
  },
  {
    path: 'local',
    name: 'local-index',
    component: () => import('../views/LocalIndexView.vue'),
    meta: meta('local-index')
  },
  ...localServiceRoutes,
  {
    path: 'portfolio',
    name: 'portfolio',
    component: () => import('../views/PortfolioView.vue'),
    meta: meta('portfolio')
  },
  {
    path: 'portfolio/:id',
    name: 'portfolio-detail',
    component: () => import('../views/PortfolioDetailView.vue'),
    meta: meta('portfolio-detail')
  },
  {
    path: 'outils',
    name: 'tools-index',
    component: () => import('../views/ToolsIndexView.vue'),
    meta: meta('tools-index')
  },
  {
    path: 'outil/audit-rgpd',
    name: 'rgpd-checker',
    component: () => import('../views/RgpdCheckerView.vue'),
    meta: meta('rgpd-checker')
  },
  {
    path: 'plan-du-site',
    name: 'html-sitemap',
    component: () => import('../views/SitemapView.vue'),
    meta: meta('html-sitemap')
  },
  {
    path: 'legal/notice',
    name: 'legal-notice',
    component: () => import('../views/legal/LegalNoticeView.vue'),
    meta: meta('legal-notice')
  },
  {
    path: 'legal/terms',
    name: 'terms',
    component: () => import('../views/legal/TermsView.vue'),
    meta: meta('terms')
  },
  {
    path: 'legal/privacy',
    name: 'privacy',
    component: () => import('../views/legal/PrivacyView.vue'),
    meta: meta('privacy')
  },
  // ─── Outils SEO / Réseau / Sécurité / Générateurs ────────────────────────────
  // SEO basique
  { path: 'outil/meta-tags', name: 'tool-meta-tags', component: () => import('../views/tools/MetaTagsView.vue'), meta: toolMeta('meta-tags') },
  { path: 'outil/robots-txt', name: 'tool-robots-txt', component: () => import('../views/tools/RobotsTxtView.vue'), meta: toolMeta('robots-txt') },

  // Technologie
  { path: 'outil/tech-detector', name: 'tool-tech-detector', component: () => import('../views/tools/TechDetectorView.vue'), meta: toolMeta('tech-detector') },
  { path: 'outil/web-server', name: 'tool-web-server', component: () => import('../views/tools/WebServerView.vue'), meta: toolMeta('web-server') },
  { path: 'outil/analytics-checker', name: 'tool-analytics-checker', component: () => import('../views/tools/AnalyticsCheckerView.vue'), meta: toolMeta('analytics-checker') },

  // Réseau & DNS
  { path: 'outil/ip-lookup', name: 'tool-ip-lookup', component: () => import('../views/tools/IpLookupView.vue'), meta: toolMeta('ip-lookup') },
  { path: 'outil/dns-lookup', name: 'tool-dns-lookup', component: () => import('../views/tools/DnsLookupView.vue'), meta: toolMeta('dns-lookup') },
  { path: 'outil/domain-age', name: 'tool-domain-age', component: () => import('../views/tools/DomainAgeView.vue'), meta: toolMeta('domain-age') },
  { path: 'outil/ping-tester', name: 'tool-ping-tester', component: () => import('../views/tools/PingTesterView.vue'), meta: toolMeta('ping-tester') },

  // E-mail
  { path: 'outil/dmarc-checker', name: 'tool-dmarc-checker', component: () => import('../views/tools/DmarcCheckerView.vue'), meta: toolMeta('dmarc-checker') },
  { path: 'outil/spf-checker', name: 'tool-spf-checker', component: () => import('../views/tools/SpfCheckerView.vue'), meta: toolMeta('spf-checker') },

  // Sécurité
  { path: 'outil/ssl-checker', name: 'tool-ssl-checker', component: () => import('../views/tools/SslCheckerView.vue'), meta: toolMeta('ssl-checker') },
  { path: 'outil/https-redirect', name: 'tool-https-redirect', component: () => import('../views/tools/HttpsRedirectView.vue'), meta: toolMeta('https-redirect') },
  { path: 'outil/htaccess-checker', name: 'tool-htaccess-checker', component: () => import('../views/tools/HtaccessCheckerView.vue'), meta: toolMeta('htaccess-checker') },
  { path: 'outil/email-scanner', name: 'tool-email-scanner', component: () => import('../views/tools/EmailScannerView.vue'), meta: toolMeta('email-scanner') },
  { path: 'outil/server-version', name: 'tool-server-version', component: () => import('../views/tools/ServerVersionView.vue'), meta: toolMeta('server-version') },

  // Social
  { path: 'outil/open-graph', name: 'tool-open-graph', component: () => import('../views/tools/OpenGraphView.vue'), meta: toolMeta('open-graph') },
  { path: 'outil/facebook-pixel', name: 'tool-facebook-pixel', component: () => import('../views/tools/FacebookPixelView.vue'), meta: toolMeta('facebook-pixel') },
  { path: 'outil/twitter-cards', name: 'tool-twitter-cards', component: () => import('../views/tools/TwitterCardsView.vue'), meta: toolMeta('twitter-cards') },

  // Performance (vue partagée, analyzer via meta)
  ...[
    { id: 'perf-speed', analyzer: 'speed' }, { id: 'perf-file-size', analyzer: 'file-size' },
    { id: 'perf-requests', analyzer: 'requests' }, { id: 'perf-amp', analyzer: 'amp' },
    { id: 'perf-js-errors', analyzer: 'js-errors' }, { id: 'perf-gzip', analyzer: 'gzip' },
    { id: 'perf-http2', analyzer: 'http2' }, { id: 'perf-images', analyzer: 'images' },
    { id: 'perf-minify', analyzer: 'minify' }, { id: 'perf-deprecated', analyzer: 'deprecated' },
    { id: 'perf-inline-css', analyzer: 'inline-css' },
  ].map(({ id, analyzer }) => ({
    path: `outil/${id}`,
    name: `tool-${id}`,
    component: () => import('../views/tools/PerformanceToolView.vue'),
    meta: { ...toolMeta(id), analyzer },
  })),

  // Convivialité (vue partagée)
  ...[
    { id: 'ux-responsive', analyzer: 'responsive' }, { id: 'ux-viewport', analyzer: 'viewport' },
    { id: 'ux-flash', analyzer: 'flash' }, { id: 'ux-iframes', analyzer: 'iframes' },
    { id: 'ux-favicon', analyzer: 'favicon' }, { id: 'ux-fonts', analyzer: 'fonts' },
    { id: 'ux-touch', analyzer: 'touch-targets' }, { id: 'ux-cwv', analyzer: 'cwv' },
  ].map(({ id, analyzer }) => ({
    path: `outil/${id}`,
    name: `tool-${id}`,
    component: () => import('../views/tools/UsabilityToolView.vue'),
    meta: { ...toolMeta(id), analyzer },
  })),

  // SEO On-Page (vue partagée)
  ...(Object.entries({
    title: 'onpage-title', 'meta-description': 'onpage-meta-description',
    hreflang: 'onpage-hreflang', 'html-lang': 'onpage-html-lang',
    canonical: 'onpage-canonical', 'serp-preview': 'onpage-serp',
    h1: 'onpage-h1', headers: 'onpage-headers',
    'keyword-density': 'onpage-keyword-density', 'google-searches': 'onpage-google-searches',
    'top-keywords': 'onpage-top-keywords', 'keyword-rank-google': 'onpage-rank-google',
    'keyword-rank-bing': 'onpage-rank-bing', 'keyword-rank-mobile': 'onpage-rank-mobile',
    'word-count': 'onpage-word-count', 'image-alt': 'onpage-image-alt',
    backlinks: 'onpage-backlinks', 'internal-links': 'onpage-internal-links',
    'seo-url': 'onpage-seo-url', 'noindex-meta': 'onpage-noindex-meta',
    'noindex-header': 'onpage-noindex-header', 'robots-check': 'onpage-robots-check',
    'sitemap-check': 'onpage-sitemap-check', 'sitemap-validate': 'onpage-sitemap-validate',
    schema: 'onpage-schema', 'url-status': 'onpage-url-status',
  } as Record<string, string>).map(([analyzer, id]) => ({
    path: `outil/${id}`,
    name: `tool-${id}`,
    component: () => import('../views/tools/OnPageToolView.vue'),
    meta: { ...toolMeta(id), analyzer },
  }))),

  // Générateurs
  { path: 'outil/gen-meta-tags', name: 'tool-gen-meta-tags', component: () => import('../views/tools/MetaTagsView.vue'), meta: { ...toolMeta('gen-meta-tags'), generatorOnly: true } },
  { path: 'outil/gen-robots-txt', name: 'tool-gen-robots-txt', component: () => import('../views/tools/RobotsTxtView.vue'), meta: toolMeta('gen-robots-txt') },
  { path: 'outil/gen-htaccess', name: 'tool-gen-htaccess', component: () => import('../views/tools/HtaccessGeneratorView.vue'), meta: toolMeta('gen-htaccess') },
  { path: 'outil/gen-sitemap', name: 'tool-gen-sitemap', component: () => import('../views/tools/SitemapGeneratorView.vue'), meta: toolMeta('gen-sitemap') },
  { path: 'outil/gen-keywords', name: 'tool-gen-keywords', component: () => import('../views/tools/KeywordGeneratorView.vue'), meta: toolMeta('gen-keywords') },
  { path: 'outil/gen-legal', name: 'tool-gen-legal', component: () => import('../views/tools/LegalGeneratorView.vue'), meta: toolMeta('gen-legal') },
  { path: 'outil/gen-cgu-cgv', name: 'tool-gen-cgu-cgv', component: () => import('../views/tools/CguCgvGeneratorView.vue'), meta: toolMeta('gen-cgu-cgv') },
  { path: 'outil/gen-specs', name: 'tool-gen-specs', component: () => import('../views/tools/SpecificationsGeneratorView.vue'), meta: toolMeta('gen-specs') },

  // Simulateurs
  { path: 'outil/sim-carbon', name: 'tool-sim-carbon', component: () => import('../views/tools/CarbonCalculatorView.vue'), meta: toolMeta('sim-carbon') },
  { path: 'outil/sim-conversion', name: 'tool-sim-conversion', component: () => import('../views/tools/ConversionSimulatorView.vue'), meta: toolMeta('sim-conversion') },
  { path: 'outil/sim-budget', name: 'tool-sim-budget', component: () => import('../views/tools/BudgetSimulatorView.vue'), meta: toolMeta('sim-budget') },

  // Diagnostic
  { path: 'outil/real-speed', name: 'tool-real-speed', component: () => import('../views/tools/RealSpeedTestView.vue'), meta: toolMeta('real-speed') },
  { path: 'outil/site-obsolete', name: 'tool-site-obsolete', component: () => import('../views/tools/SiteObsolescenceView.vue'), meta: toolMeta('site-obsolete') },
  { path: 'outil/wp-security', name: 'tool-wp-security', component: () => import('../views/tools/WordPressSecurityView.vue'), meta: toolMeta('wp-security') },

  localCityHubRoute,
  localCommuneLegacyRedirect
]

// Routes privées (sans préfixe de langue)
export const privateRoutes: RouteRecordRaw[] = [
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('../views/DashboardView.vue'),
    meta: { ...meta('dashboard'), requiresAuth: true }
  },
  {
    path: '/account',
    name: 'account',
    component: () => import('../views/AccountView.vue'),
    meta: { ...meta('account'), requiresAuth: true }
  },
  {
    path: '/new-project',
    name: 'new-project',
    component: () => import('../views/NewProjectView.vue'),
    meta: { ...meta('new-project'), requiresAuth: true }
  },
  {
    path: '/thank-you',
    name: 'thank-you',
    component: () => import('../views/ThankYouView.vue'),
    meta: meta('thank-you')
  },
  {
    path: '/projects/:slug',
    name: 'project-detail',
    component: () => import('../views/ProjectDetailView.vue'),
    meta: { ...meta('project-detail'), requiresAuth: true }
  },
  {
    path: '/my-projects',
    name: 'user-projects',
    component: () => import('../views/UserProjectsView.vue'),
    meta: { ...meta('user-projects'), requiresAuth: true }
  }
]

// Routes d'authentification (sans préfixe de langue)
export const authRoutes: RouteRecordRaw[] = [
  {
    path: '/auth/login',
    name: 'login',
    component: () => import('../views/auth/LoginView.vue'),
    meta: meta('login')
  },
  {
    path: '/auth/signup',
    name: 'signup',
    component: () => import('../views/auth/SignupView.vue'),
    meta: meta('signup')
  },
  {
    path: '/auth/forgot-password',
    name: 'forgot-password',
    component: () => import('../views/auth/ForgotPasswordView.vue'),
    meta: meta('forgot-password')
  },
  {
    path: '/auth/reset-password',
    name: 'reset-password',
    component: () => import('../views/auth/ResetPasswordView.vue'),
    meta: meta('reset-password')
  },
  {
    path: '/auth/verify-email',
    name: 'verify-email',
    component: () => import('../views/auth/VerifyEmailView.vue'),
    meta: meta('verify-email')
  }
]

// Les routes d'administration ont été déplacées vers bo/ (sous-domaine bo.postdev.fr)
export const adminRoutes: RouteRecordRaw[] = []
